import React, { useEffect, useState } from 'react';
import { AlertTriangle, BookOpen, CheckCircle2, RefreshCw, Save, ShieldCheck, Users } from 'lucide-react';
import { operatingState } from '../../services/api';

const MetricTile = ({ label, value, detail, tone = 'slate' }) => {
    const colors = {
        slate: { bg: '#f8fafc', border: '#e2e8f0', text: '#0f172a' },
        amber: { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
        emerald: { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46' },
        sky: { bg: '#f0f9ff', border: '#bae6fd', text: '#075985' },
    };
    const color = colors[tone] || colors.slate;

    return (
        <div style={{
            border: `1px solid ${color.border}`,
            background: color.bg,
            borderRadius: 8,
            padding: 16,
            minHeight: 112,
        }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                {label}
            </div>
            <div style={{ marginTop: 10, fontSize: 28, fontWeight: 800, color: color.text }}>
                {value}
            </div>
            {detail && (
                <div style={{ marginTop: 6, fontSize: 13, color: '#475569', lineHeight: 1.4 }}>
                    {detail}
                </div>
            )}
        </div>
    );
};

const GuardrailRow = ({ icon, label, value }) => {
    const IconComponent = icon;

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 0',
            borderBottom: '1px solid #e2e8f0',
        }}>
            <IconComponent size={16} color="#0f766e" />
            <span style={{ flex: 1, fontSize: 14, color: '#334155' }}>{label}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{value}</span>
        </div>
    );
};

const buildConfigDraft = (configPack = {}, scope = {}) => ({
    name: configPack.name || 'Phase 3 operating guardrails',
    scope: configPack.scope || (scope.campusId ? 'campus' : 'district'),
    campus_id: configPack.campus_id || scope.campusId || '',
    grade_cadence_rules: {
        minorRequiredPerWindow: configPack.gradeCadenceRules?.minorRequiredPerWindow ?? 2,
        majorRequiredPerWindow: configPack.gradeCadenceRules?.majorRequiredPerWindow ?? 1,
        warningWindowDays: configPack.gradeCadenceRules?.warningWindowDays ?? 5,
    },
    communication_guardrails: {
        teacherApprovalRequired: true,
        sendAutomationAllowed: false,
        directStudentIdentifiersAllowed: false,
        draftOnly: true,
    },
    student_signal_rules: {
        pseudonymousStudentRefRequired: true,
        directIdentifiersRejected: true,
        highSensitivityTermsRejected: true,
        automationRequiresTeacherApproval: true,
    },
});

const ConfigPackEditor = ({ draft, onChange, onSave, saving, message, error }) => {
    if (!draft) return null;

    const updateDraft = (updates) => onChange({ ...draft, ...updates });
    const updateCadence = (key, value) => onChange({
        ...draft,
        grade_cadence_rules: {
            ...draft.grade_cadence_rules,
            [key]: Math.max(Number(value || 0), 0),
        },
    });

    return (
        <section style={{
            marginTop: 16,
            border: '1px solid #dbeafe',
            background: '#fff',
            borderRadius: 8,
            padding: 18,
        }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 14 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                        Config Pack Management
                    </h2>
                    <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 13, lineHeight: 1.45 }}>
                        Create a staging-safe district or campus guardrail revision. Safety controls are locked on save.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onSave}
                    disabled={saving}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        border: '1px solid #0f766e',
                        background: saving ? '#ccfbf1' : '#0f766e',
                        color: saving ? '#0f766e' : '#fff',
                        borderRadius: 8,
                        padding: '10px 12px',
                        fontWeight: 800,
                        cursor: saving ? 'wait' : 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <Save size={15} />
                    {saving ? 'Saving...' : 'Save Pack'}
                </button>
            </div>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 10,
            }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' }}>
                    Name
                    <input
                        value={draft.name}
                        onChange={(event) => updateDraft({ name: event.target.value })}
                        style={{ height: 40, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 10px', fontSize: 14 }}
                    />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' }}>
                    Scope
                    <select
                        value={draft.scope}
                        onChange={(event) => updateDraft({ scope: event.target.value })}
                        style={{ height: 40, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 10px', fontSize: 14, background: '#fff' }}
                    >
                        <option value="campus">Campus</option>
                        <option value="district">District</option>
                    </select>
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' }}>
                    Minor/window
                    <input
                        type="number"
                        min="0"
                        value={draft.grade_cadence_rules.minorRequiredPerWindow}
                        onChange={(event) => updateCadence('minorRequiredPerWindow', event.target.value)}
                        style={{ height: 40, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 10px', fontSize: 14 }}
                    />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' }}>
                    Major/window
                    <input
                        type="number"
                        min="0"
                        value={draft.grade_cadence_rules.majorRequiredPerWindow}
                        onChange={(event) => updateCadence('majorRequiredPerWindow', event.target.value)}
                        style={{ height: 40, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 10px', fontSize: 14 }}
                    />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' }}>
                    Warning days
                    <input
                        type="number"
                        min="0"
                        value={draft.grade_cadence_rules.warningWindowDays}
                        onChange={(event) => updateCadence('warningWindowDays', event.target.value)}
                        style={{ height: 40, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 10px', fontSize: 14 }}
                    />
                </label>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {[
                    'Teacher approval required',
                    'Send automation blocked',
                    'Direct student identifiers blocked',
                    'Student signals pseudonymous only',
                ].map((label) => (
                    <span
                        key={label}
                        style={{
                            border: '1px solid #99f6e4',
                            background: '#f0fdfa',
                            color: '#115e59',
                            borderRadius: 999,
                            padding: '5px 8px',
                            fontSize: 12,
                            fontWeight: 800,
                        }}
                    >
                        {label}
                    </span>
                ))}
            </div>

            {message && <div style={{ marginTop: 10, color: '#047857', fontSize: 13, fontWeight: 700 }}>{message}</div>}
            {error && <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>{error}</div>}
        </section>
    );
};

const TeacherReadinessList = ({ teachers = [] }) => {
    if (teachers.length === 0) return null;

    return (
        <section style={{
            marginTop: 16,
            border: '1px solid #e2e8f0',
            background: '#fff',
            borderRadius: 8,
            padding: 18,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <Users size={18} color="#2563eb" />
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                    Teacher Readiness
                </h2>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
                {teachers.map((teacher) => (
                    <div
                        key={`${teacher.teacherEmail}-${teacher.campusId || 'district'}`}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                            gap: 10,
                            alignItems: 'center',
                            border: '1px solid #f1f5f9',
                            borderRadius: 8,
                            padding: 10,
                            background: '#f8fafc',
                        }}
                    >
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{teacher.teacherEmail}</div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>{teacher.campusId || 'district scope'}</div>
                        </div>
                        <div style={{ fontSize: 12, color: '#334155' }}>{teacher.courseCount} courses</div>
                        <div style={{ fontSize: 12, color: teacher.profileComplete ? '#047857' : '#b45309', fontWeight: 700 }}>
                            {teacher.profileComplete ? 'Profile ready' : 'Profile missing'}
                        </div>
                        <div style={{ fontSize: 12, color: '#334155' }}>{teacher.pendingSignalCount} pending</div>
                        <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.35 }}>{teacher.nextStep}</div>
                    </div>
                ))}
            </div>
        </section>
    );
};

const OperatingStateTab = ({ filters = {} }) => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [configDraft, setConfigDraft] = useState(null);
    const [savingPack, setSavingPack] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [saveError, setSaveError] = useState('');

    const loadSummary = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const response = await operatingState.getAdminSummary(params);
            setSummary(response.data);
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Operating-state summary unavailable');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.district_id, filters.campus_id]);

    const data = summary?.summary || {};
    const privacyBoundary = summary?.privacyBoundary || {};
    const access = summary?.roleAwareAccess || {};
    const configPack = summary?.configPack || {};
    const communicationGuardrails = configPack.communicationGuardrails || {};
    const studentSignalRules = configPack.studentSignalRules || {};

    useEffect(() => {
        if (summary) {
            setConfigDraft(buildConfigDraft(summary.configPack, summary.scope));
        }
    }, [summary]);

    const saveConfigPack = async () => {
        if (!configDraft) return;

        setSavingPack(true);
        setSaveMessage('');
        setSaveError('');
        try {
            const response = await operatingState.saveConfigPack(configDraft);
            setSummary((current) => ({
                ...(current || {}),
                configPack: response.data?.configPack || current?.configPack,
            }));
            setSaveMessage('Config pack saved with Phase 3 guardrails locked.');
        } catch (err) {
            setSaveError(err?.response?.data?.detail || err?.message || 'Config pack save failed');
        } finally {
            setSavingPack(false);
        }
    };

    return (
        <div style={{ maxWidth: 1120 }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                marginBottom: 20,
            }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0 }}>
                        Operating State
                    </h1>
                    <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>
                        District and campus readiness for Phase 3 teacher-reviewed autonomy.
                    </p>
                </div>
                <button
                    onClick={loadSummary}
                    disabled={loading}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        border: '1px solid #cbd5e1',
                        background: '#fff',
                        color: '#0f172a',
                        borderRadius: 8,
                        padding: '10px 12px',
                        fontWeight: 700,
                        cursor: loading ? 'wait' : 'pointer',
                    }}
                >
                    <RefreshCw size={15} />
                    Refresh
                </button>
            </div>

            {error && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    border: '1px solid #fecaca',
                    background: '#fef2f2',
                    color: '#991b1b',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 16,
                }}>
                    <AlertTriangle size={16} />
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{error}</span>
                </div>
            )}

            {loading && !summary ? (
                <div style={{ color: '#64748b', fontSize: 14 }}>Loading operating-state summary...</div>
            ) : (
                <>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                        gap: 12,
                        marginBottom: 20,
                    }}>
                        <MetricTile
                            label="Teachers"
                            value={data.teacherCount ?? 0}
                            detail={`${data.teachersWithCourses ?? 0} with course context`}
                            tone="sky"
                        />
                        <MetricTile
                            label="Active Courses"
                            value={data.activeCourseCount ?? 0}
                            detail="From teacher-owned profile/course records"
                            tone="emerald"
                        />
                        <MetricTile
                            label="Missing Profiles"
                            value={data.missingProfileCount ?? 0}
                            detail="Blocks truthful proactive planning"
                            tone={(data.missingProfileCount ?? 0) > 0 ? 'amber' : 'emerald'}
                        />
                        <MetricTile
                            label="Pending Signals"
                            value={data.studentSignalsPendingApproval ?? 0}
                            detail="Pseudonymous, teacher-review required"
                            tone={(data.studentSignalsPendingApproval ?? 0) > 0 ? 'amber' : 'slate'}
                        />
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                        gap: 16,
                    }}>
                        <section style={{
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                            borderRadius: 8,
                            padding: 18,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                <ShieldCheck size={18} color="#0f766e" />
                                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                                    FERPA Guardrails
                                </h2>
                            </div>
                            <GuardrailRow
                                icon={CheckCircle2}
                                label="Teacher approval required"
                                value={communicationGuardrails.teacherApprovalRequired === false ? 'No' : 'Yes'}
                            />
                            <GuardrailRow
                                icon={ShieldCheck}
                                label="Send automation allowed"
                                value={communicationGuardrails.sendAutomationAllowed ? 'Yes' : 'No'}
                            />
                            <GuardrailRow
                                icon={Users}
                                label="Student details returned"
                                value={privacyBoundary.studentSignalDetailsSuppressed === false ? 'Yes' : 'No'}
                            />
                            <GuardrailRow
                                icon={BookOpen}
                                label="Student ref rule"
                                value={studentSignalRules.pseudonymousStudentRefRequired === false ? 'Open' : 'Pseudonymous only'}
                            />
                        </section>

                        <section style={{
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                            borderRadius: 8,
                            padding: 18,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                <Users size={18} color="#2563eb" />
                                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                                    Role-Aware Scope
                                </h2>
                            </div>
                            <GuardrailRow
                                icon={ShieldCheck}
                                label="District admin can filter campus"
                                value={access.districtAdminCanFilterCampus ? 'Yes' : 'No'}
                            />
                            <GuardrailRow
                                icon={ShieldCheck}
                                label="Campus admin locked to campus"
                                value={access.campusAdminLockedToCampus ? 'Yes' : 'No'}
                            />
                            <GuardrailRow
                                icon={CheckCircle2}
                                label="Read model version"
                                value={summary?.readModelVersion || 'unknown'}
                            />
                            <GuardrailRow
                                icon={BookOpen}
                                label="Config pack"
                                value={configPack.name || 'Default guardrails'}
                            />
                        </section>
                    </div>

                    {!!summary?.warnings?.length && (
                        <section style={{
                            marginTop: 16,
                            border: '1px solid #fde68a',
                            background: '#fffbeb',
                            borderRadius: 8,
                            padding: 14,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                <AlertTriangle size={16} color="#b45309" />
                                <strong style={{ color: '#92400e', fontSize: 14 }}>Readiness warnings</strong>
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 20, color: '#92400e', fontSize: 13 }}>
                                {summary.warnings.map((warning) => (
                                    <li key={warning.code}>{warning.message}</li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <TeacherReadinessList teachers={summary?.teacherReadiness || []} />

                    <ConfigPackEditor
                        draft={configDraft}
                        onChange={setConfigDraft}
                        onSave={saveConfigPack}
                        saving={savingPack}
                        message={saveMessage}
                        error={saveError}
                    />
                </>
            )}
        </div>
    );
};

export default OperatingStateTab;
